/**
 * Application Constants and Configuration
 * 
 * Centralized configuration for the invoice generator application.
 * All magic numbers and hardcoded values should be defined here.
 */

// ============================================================================
// API CONFIGURATION
// ============================================================================

/**
 * Base API URL - defaults to localhost for development
 */
export const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

/**
 * API endpoints
 */
export const API_ENDPOINTS = Object.freeze({
  // GENERATE_INVOICE removed - deprecated in Feature #7, use INVOICE_FINALIZE instead
  HEALTH: '/api/health',                       // Liveness probe / cold-start pre-warm
  TAX_RATES: '/api/tax-rates',
  CURRENCIES: '/api/currencies',
  INVOICE_TYPES: '/api/invoice-types',        // List all invoice types
  INVOICE_TYPE: '/api/invoice-types/',        // Get specific type config (append typeId)
  INVOICE_VALIDATE: '/api/invoices/validate', // Validate invoice draft (Feature #7)
  INVOICE_CALCULATE: '/api/invoices/calculate', // Calculate totals (Feature #7)
  INVOICE_FINALIZE: '/api/invoices/finalize',  // Finalize and generate PDF (Feature #7)
  INVOICE_DRAFT: '/api/invoices/draft',        // Save/update a draft (Pro)
  INVOICE_DRAFTS: '/api/invoices/drafts',      // List drafts (Pro)
  ENTITLEMENTS: '/api/entitlements',           // Tenant entitlements (Feature #15)
});

/**
 * API request timeout in milliseconds.
 * Set to 90s to survive a Render free-tier cold start (~30-60s).
 */
export const API_TIMEOUT = 90000;

// ============================================================================
// INVOICE DEFAULTS
// ============================================================================

/**
 * Default payment terms in days
 */
export const DEFAULT_PAYMENT_TERMS_DAYS = 30;

/**
 * Default invoice terms text
 */
export const DEFAULT_TERMS_TEXT = 'Payment is due within 30 days. Late payments may incur fees.';

/**
 * Maximum number of line items allowed
 */
export const MAX_LINE_ITEMS = 50;

/**
 * Minimum number of line items required
 */
export const MIN_LINE_ITEMS = 1;

// ============================================================================
// AUTOSAVE CONFIGURATION
// ============================================================================

/**
 * LocalStorage key for autosaved drafts
 */
export const AUTOSAVE_KEY_PREFIX = 'invoice_draft';
export const getAutoSaveKey = (invoiceType) => `${AUTOSAVE_KEY_PREFIX}_${invoiceType || 'freelancer'}`;

/**
 * Autosave delay in milliseconds
 */
export const AUTOSAVE_DELAY_MS = 2000;

/**
 * Maximum age for autosaved data in milliseconds (7 days)
 */
export const AUTOSAVE_MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;

// ============================================================================
// UI CONFIGURATION
// ============================================================================

/**
 * A4 aspect ratio (width/height)
 */
export const A4_ASPECT_RATIO = 210 / 297;

/**
 * Maximum canvas width in pixels
 */
export const MAX_CANVAS_WIDTH = 794;

/**
 * Maximum logo file size in bytes (2MB)
 */
export const MAX_LOGO_SIZE_BYTES = 2 * 1024 * 1024;

/**
 * Logo resize dimensions
 */
export const LOGO_MAX_DIMENSIONS = Object.freeze({
  width: 400,
  height: 400,
});

/**
 * Undo history size
 */
export const UNDO_HISTORY_SIZE = 10;

// ============================================================================
// COUNTRIES LIST
// ============================================================================

/**
 * Supported countries for tax rate selection
 */
export const COUNTRIES = Object.freeze([
  'United States',
  'United Kingdom',
  'Canada',
  'Australia',
  'India',
  'Germany',
  'France',
  'Spain',
  'Italy',
  'Netherlands',
  'Singapore',
  'UAE',
  'Saudi Arabia',
  'Japan',
  'China',
]);

// ============================================================================
// DEFAULT TAX RATES (Fallback - NON-AUTHORITATIVE)
// ============================================================================

/**
 * Default tax rates by country - FALLBACK ONLY
 * 
 * ⚠️ WARNING: These are NOT authoritative values.
 * Backend is the SINGLE SOURCE OF TRUTH for tax rates.
 * These values are only used if the backend API is unreachable.
 * 
 * For production, the UI should show a loading/error state
 * rather than silently substituting these values.
 * 
 * @deprecated Prefer fetching from backend API via loadTaxRates()
 */
export const DEFAULT_TAX_RATES = Object.freeze({
  'United States': 0,
  'United Kingdom': 20,
  'Canada': 5,
  'Australia': 10,
  'India': 18,
  'Germany': 19,
  'France': 20,
  'Spain': 21,
  'Italy': 22,
  'Netherlands': 21,
  'Singapore': 9,
  'UAE': 5,
  'Saudi Arabia': 15,
  'Japan': 10,
  'China': 13,
});

// ============================================================================
// DEFAULT CURRENCIES (Fallback - NON-AUTHORITATIVE)
// ============================================================================

/**
 * Default currencies - FALLBACK ONLY
 * 
 * ⚠️ WARNING: These are NOT authoritative values.
 * Backend is the SINGLE SOURCE OF TRUTH for currency info.
 * These values are only used if the backend API is unreachable.
 * 
 * For production, the UI should show a loading/error state
 * rather than silently substituting these values.
 * 
 * @deprecated Prefer fetching from backend API via loadCurrencies()
 */
export const DEFAULT_CURRENCIES = Object.freeze({
  USD: '$',
  EUR: '€',
  GBP: '£',
  INR: '₹',
  AUD: 'A$',
  CAD: 'C$',
  SGD: 'S$',
  AED: 'AED',
  SAR: 'SAR',
  JPY: '¥',
  CNY: '¥',
  CHF: 'CHF',
  SEK: 'kr',
  NZD: 'NZ$',
});

// ============================================================================
// API DATA LOADERS
// ============================================================================

/**
 * Fetch tax rates from API with fallback
 * 
 * ⚠️ NOTE: Backend is the single source of truth.
 * Fallback to DEFAULT_TAX_RATES only when API is unavailable.
 * Consider showing a loading/error state in UI instead of silent fallback.
 * 
 * @returns {Promise<{data: Object, isFromBackend: boolean}>} Tax rates by country and source indicator
 */
export async function loadTaxRates() {
  try {
    const response = await fetch(`${API_URL}${API_ENDPOINTS.TAX_RATES}`, {
      signal: AbortSignal.timeout(API_TIMEOUT),
    });
    
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`);
    }
    
    const data = await response.json();
    
    // Convert API response to simple {country: rate} format
    const taxRates = {};
    Object.entries(data).forEach(([country, info]) => {
      taxRates[country] = info.rate ?? info;
    });
    
    return { data: taxRates, isFromBackend: true };
  } catch (error) {
    console.error(
      '[Feature #15] Failed to load tax rates from backend. Using NON-AUTHORITATIVE fallback:',
      error.message
    );
    return { data: { ...DEFAULT_TAX_RATES }, isFromBackend: false };
  }
}

/**
 * Fetch currencies from API with fallback
 * 
 * ⚠️ NOTE: Backend is the single source of truth.
 * Fallback to DEFAULT_CURRENCIES only when API is unavailable.
 * Consider showing a loading/error state in UI instead of silent fallback.
 * 
 * @returns {Promise<{data: Object, isFromBackend: boolean}>} Currencies {code: symbol} and source indicator
 */
export async function loadCurrencies() {
  try {
    const response = await fetch(`${API_URL}${API_ENDPOINTS.CURRENCIES}`, {
      signal: AbortSignal.timeout(API_TIMEOUT),
    });
    
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`);
    }
    
    const data = await response.json();
    
    // Convert API response to simple {code: symbol} format
    const currencies = {};
    Object.entries(data).forEach(([code, info]) => {
      currencies[code] = info.symbol ?? info;
    });
    
    return { data: currencies, isFromBackend: true };
  } catch (error) {
    console.error(
      '[Feature #15] Failed to load currencies from backend. Using NON-AUTHORITATIVE fallback:',
      error.message
    );
    return { data: { ...DEFAULT_CURRENCIES }, isFromBackend: false };
  }
}

/**
 * Load all app data (tax rates and currencies)
 * 
 * @returns {Promise<{taxRates: Object, currencies: Object, allFromBackend: boolean}>}
 */
export async function loadAppData() {
  const [taxRatesResult, currenciesResult] = await Promise.all([
    loadTaxRates(),
    loadCurrencies(),
  ]);
  
  return { 
    taxRates: taxRatesResult.data, 
    currencies: currenciesResult.data,
    allFromBackend: taxRatesResult.isFromBackend && currenciesResult.isFromBackend,
  };
}

// ============================================================================
// EXPORTS
// ============================================================================

const constantsExports = {
  API_URL,
  API_ENDPOINTS,
  API_TIMEOUT,
  DEFAULT_PAYMENT_TERMS_DAYS,
  DEFAULT_TERMS_TEXT,
  MAX_LINE_ITEMS,
  MIN_LINE_ITEMS,
  AUTOSAVE_KEY_PREFIX,
  getAutoSaveKey,
  AUTOSAVE_DELAY_MS,
  AUTOSAVE_MAX_AGE_MS,
  A4_ASPECT_RATIO,
  MAX_CANVAS_WIDTH,
  MAX_LOGO_SIZE_BYTES,
  LOGO_MAX_DIMENSIONS,
  UNDO_HISTORY_SIZE,
  COUNTRIES,
  DEFAULT_TAX_RATES,
  DEFAULT_CURRENCIES,
  loadTaxRates,
  loadCurrencies,
  loadAppData,
};

export default constantsExports;